/**
 * @fileoverview Summary metrics row for the Task Tracker matrix.
 *
 * Totalizers use pending semantics (AR-912): each counter reads
 * "<label> pending: <pending>/<total>", matching what the API returns.
 */

import { Anchor, Group, Paper, Stack, Text } from '@mantine/core'
import { IconExternalLink } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { useMediaQuery, useMantineTheme } from 'hooks'
import { CompletedBadge } from './CompletedBadge'
import type { TaskTrackSummary, TaskTrackSummaryStat } from '../types'

interface TaskTrackerSummaryProps {
  summary: TaskTrackSummary
  /** Optional — hidden when omitted (e.g. embedded inside the project view). */
  projectId?: string
  /** Optional — hidden when omitted. */
  projectName?: string
  /** When true, renders a "Completed" tag next to the project name. */
  projectCompleted?: boolean
  /** AR-924: number of deliverables uploaded by a teacher; hidden when undefined. */
  uploadedByTeacherCount?: number
}

const ITEM_STYLE = { wordBreak: 'break-word' as const, minWidth: 0 }
const PROJECT_NAME_MAX_WIDTH = 320

export function TaskTrackerSummary({
  summary,
  projectId,
  projectName,
  projectCompleted = false,
  uploadedByTeacherCount,
}: TaskTrackerSummaryProps) {
  const { t } = useTranslation()
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)

  const hasAssessments = summary.selfAssessments.total > 0
  const hasDeliverables = summary.deliverables.total > 0

  if (!hasAssessments && !hasDeliverables) return null

  const items = [
    hasAssessments && {
      key: 'self',
      label: t('task_tracker.summary.self_assessments'),
      stat: summary.selfAssessments,
    },
    hasAssessments && {
      key: 'teacher',
      label: t('task_tracker.summary.teacher_assessments'),
      stat: summary.teacherAssessments,
    },
    hasDeliverables && {
      key: 'deliverables',
      label: t('task_tracker.summary.deliverables'),
      stat: summary.deliverables,
    },
  ].filter((item): item is { key: string; label: string; stat: TaskTrackSummaryStat } => Boolean(item))

  const header = (
    <Group spacing="sm" align="center" noWrap={!isMobile} style={{ minWidth: 0 }}>
      {projectName && (
        <Text
          size="sm"
          weight={600}
          lineClamp={2}
          style={{ maxWidth: isMobile ? undefined : PROJECT_NAME_MAX_WIDTH }}
        >
          {projectName}
        </Text>
      )}
      {projectCompleted && <CompletedBadge />}
      {projectId && (
        <Anchor
          href={`/projects/${projectId}`}
          target="_blank"
          size="xs"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}
        >
          {t('task_tracker.table.view_project')}
          <IconExternalLink size={12} />
        </Anchor>
      )}
    </Group>
  )

  const stats = items.map(({ key, label, stat }) => {
    const nothingPending = stat.total > 0 && stat.pending === 0
    return (
      <Text key={key} size="sm" color="dimmed" style={ITEM_STYLE}>
        {label}:{' '}
        <Text span weight={600} color={nothingPending ? 'teal' : 'dark'}>
          {stat.pending}/{stat.total}
        </Text>
      </Text>
    )
  })

  // AR-924: plain count (no "/total") — mirrors the Figma "Uploaded by teacher: 4".
  if (hasDeliverables && uploadedByTeacherCount !== undefined) {
    stats.push(
      <Text key="uploaded_by_teacher" size="sm" color="dimmed" style={ITEM_STYLE}>
        {t('task_tracker.summary.uploaded_by_teacher')}:{' '}
        <Text span weight={600} color="dark">{uploadedByTeacherCount}</Text>
      </Text>,
    )
  }

  return (
    <Paper withBorder radius="md" p="md">
      {isMobile ? (
        <Stack spacing="sm">
          {header}
          <Stack spacing={4}>{stats}</Stack>
        </Stack>
      ) : (
        <Group position="apart" align="center" noWrap>
          {header}
          <Group spacing="xl" noWrap style={{ minWidth: 0 }}>
            {stats}
          </Group>
        </Group>
      )}
    </Paper>
  )
}
